// @ts-check
if (!window.AndDaughter) {
  window.AndDaughter = {};
}

window.AndDaughter.video = function () {
  /**
   * @var {Record<string, string>} a list of selectors used to match elements in the DOM
   */
  const selectors = {
    manuallyPaused: "data-has-been-manually-paused",
  };

  /**
   * @var {NodeListOf<HTMLVideoElement>} the <video> tag
   */
  const players = document.querySelectorAll("[data-js-video], .data-js-video");

  /**
   * @var {<NodeListOf<HTMLButtonElement | HTMLAnchorElement>} the <button> that when clicked, plays/pauses the video
   */
  const buttons = document.querySelectorAll("[data-js-video-button]");

  /**
   * @var {MediaQueryList { media: string; matches: boolean; onchange?:  () => void; }}
   */
  const mediaQueryPrefersReduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  /**
   * get an instance of the video player from the button
   * @param {HTMLButtonElement} button - the clicked button
   *
   * @returns {HTMLVideoElement | null}
   */
  const getPlayerFromButton = (button) => {
    return document.getElementById(`${button?.getAttribute("aria-controls")}`);
  };

  /**
   * get an instance of the controlling button from the video player
   * @param {HTMLVideoElement} player - the <video> element
   *
   * @returns {HTMLButtonElement | null}
   */
  const getButtonFromPlayer = (player) => {
    return document.querySelector(`[aria-controls="${player.id}"]`);
  };

  /**
   * pause (stop) the video and update the button
   * @param {HTMLButtonElement} button - the clicked button
   *
   * @returns {void}
   */
  const pause = (button) => {
    const player = getPlayerFromButton(button);
    player?.pause();
    button?.querySelector(".pause")?.setAttribute("hidden", "true");
    button?.querySelector(".play")?.removeAttribute("hidden");
  };

  /**
   * play the video and update the button
   * @param {HTMLButtonElement} button - the clicked button
   *
   * @returns {void}
   */
  const play = (button) => {
    const player = getPlayerFromButton(button);
    player?.play();
    button.querySelector(".pause")?.removeAttribute("hidden");
    button.querySelector(".play")?.setAttribute("hidden", "true");
  };

  /**
   * pause the video when it goes out of view and play it
   * (unless it has been manually stopped) when it comes back into view
   * @param {IntersectionObserverEntry[]} entries,
   * @param {IntersectionObserver} observer
   *
   * @returns {void}
   */
  function handleIntersect(entries, observer) {
    entries.forEach((entry) => {
      const button = getButtonFromPlayer(entry.target);
      const hasBeenManuallyPaused =
        entry.target.getAttribute(selectors.manuallyPaused)?.toString() ===
        "true";
      if (button) {
        if (!entry.isIntersecting) {
          pause(button);
        } else {
          if (!hasBeenManuallyPaused) {
            play(button);
          }
        }
      }
    });
  }

  /**
   * Set up an IntersectionObsever instance and track whether the video element
   * is in the viewport
   *
   * @returns {void}
   */
  function createIntersectionObserver() {
    let options = {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    };

    let observer = new IntersectionObserver(handleIntersect, options);
    players.forEach((player) => {
      observer.observe(player);
      player.setAttribute(selectors.manuallyPaused, "false");
    });
  }

  /**
   * Handle what happens when the user clicks the button. Based upon the status of the video
   * we either play or pause the video. On pause, add a manual check via a data attribute to let
   * us know that the user has opted to not play this video. That way we won't autoplay it next time
   * it comes into view
   * @param {Event} event
   *
   * @returns {void}
   */
  const handleClick = (event) => {
    const button = event.currentTarget;
    const player = getPlayerFromButton(button);

    if (!button || !player) {
      return;
    }

    if (player?.paused) {
      play(button);
    } else {
      pause(button);
      player.setAttribute(selectors.manuallyPaused, "true");
    }
  };

  /**
   * Prevents autoplay for user who have chosen to receive less motion in their OS/browser
   *
   * @returns {void}
   */
  const preventAutoPlay = () => {
    players.forEach((player) => {
      player.removeAttribute("autoplay");
      pause(getButtonFromPlayer(player));
    });
  };

  /**
   * Add and remove event listeners that will trigger actions on the elements
   *
   * @returns {void}
   */
  const addEventListeners = () => {
    for (let i = 0; buttons.length > i; i++) {
      buttons[i].removeEventListener("click", handleClick);
      buttons[i].addEventListener("click", handleClick);
    }
  };

    /**
   * Handle unmute button clicks
   * @returns {void}
   */
  const addUnmuteEventListeners = () => {
    const unmuteButtons = document.querySelectorAll("[data-js-unmute-button]");
    
    const toggleMute = (button) => {
      const player = getPlayerFromButton(button);
      if (!player) return;
      
      if (player.muted) {
        player.muted = false;
        button.querySelector(".unmute")?.setAttribute("hidden", "true");
        button.querySelector(".mute")?.removeAttribute("hidden");
      } else {
        player.muted = true;
        button.querySelector(".mute")?.setAttribute("hidden", "true");
        button.querySelector(".unmute")?.removeAttribute("hidden");
      }
    };

    for (let i = 0; i < unmuteButtons.length; i++) {
      unmuteButtons[i].addEventListener("click", function(e) {
        toggleMute(e.currentTarget);
      });
    }
    };

  /**
   * Scripts/tasks that run when object is first created
   *
   * @returns {void}
   */
  const init = () => {
    if (players.length === 0 || buttons.length === 0) {
      return;
    }

    if (mediaQueryPrefersReduceMotion.matches === true) {
      preventAutoPlay();
    }

    addEventListeners();
    addUnmuteEventListeners();

    // Remove the controls because we are going to have our own 'simple' play/pause buttons
    players.forEach((player) => {
      player.setAttribute("tabindex", "-1");
    });

    // jackpot bonus, we're going to pause the video when it's out of view
    if (
      "IntersectionObserver" in window &&
      mediaQueryPrefersReduceMotion.matches === false
    ) {
      createIntersectionObserver();
    }
  };

  return {
    init,
  };
};

window.AndDaughter.video().init();
